import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import StarRating from '../ui/StarRating';

const ReviewForm = ({ isOpen, onClose, vendorId, vendorName, type = 'vendor', productId = null }) => {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (data) => {
      const endpoint = type === 'vendor' 
        ? `/vendors/${vendorId}/reviews`
        : `/products/${productId}/reviews`;
      return api.post(endpoint, data);
    },
    {
      onSuccess: () => {
        toast.success('تم إضافة التقييم بنجاح');
        queryClient.invalidateQueries(['vendor-reviews', vendorId]);
        queryClient.invalidateQueries(['product-reviews', productId]);
        queryClient.invalidateQueries('vendors');
        handleClose();
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'حدث خطأ أثناء إضافة التقييم';
        toast.error(message);
      }
    }
  );

  const handleClose = () => {
    setRating(0);
    setTitle('');
    setComment('');
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('الرجاء اختيار التقييم');
      return;
    }

    mutation.mutate({
      rating,
      title: title.trim() || null,
      comment: comment.trim() || null
    });
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      {/* Full-screen container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <Dialog.Title className="text-xl font-bold text-gray-900">
              تقييم {type === 'vendor' ? vendorName : 'المنتج'}
            </Dialog.Title>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                التقييم <span className="text-red-500">*</span>
              </label>
              <div className="flex justify-center">
                <StarRating
                  rating={rating}
                  interactive={true}
                  onChange={setRating}
                  size="xl"
                />
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                عنوان التقييم (اختياري)
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="مثال: منتجات ممتازة وخدمة رائعة"
                maxLength={255}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Comment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                تعليقك (اختياري)
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="شارك تجربتك مع الآخرين..."
                rows={4}
                maxLength={1000}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                {comment.length}/1000 حرف
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={mutation.isLoading || rating === 0}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mutation.isLoading ? 'جاري الإرسال...' : 'إرسال التقييم'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ReviewForm;
